import styled from '@emotion/styled'
import { Button, CircularProgress, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { useActiveOrganization } from 'hooks/use-organization'
import { useEffect, useState } from 'react'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { authenticatedUserAtom, toastAtom } from 'state'
import FloatingButtonBar from 'components/FloatingButtonBar'
import { COLORS } from 'app-constants'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { StyledCheckboxWithDarkMode } from 'components/MyTwin/AddOrEditInstructionModal'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import { useFetchChannelsConfig } from 'hooks/use-slack'
import { OnboardingStep, SourceSlackChannelConfig } from 'api/model'
import { slackBuildInsights, sourcesUpdateChannelsConfig } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import JelledModal from 'components/JelledModal'
import withOnboardingStep, { UpdateOnboardingStepOption } from 'components/FTUE/withOnboardingStep'
import { myTwinAtom } from 'state/my-twin'

export const PageContainerStack = styled(Stack)`
  border-left: 1px solid #ddd;
  width: 100%;
  height: 100vh;
  padding: 24px;
  background-color: #f8f8f8;
`

const ChannelRow = ({
  channel,
  isSelected,
  onToggleSelectedChannel,
}: {
  channel: SourceSlackChannelConfig
  isSelected: boolean
  onToggleSelectedChannel: (channelId: string, isChecked: boolean) => void
}) => {
  const theme = useTheme()

  const onCheckChannel = (event: React.ChangeEvent<HTMLInputElement>) => {
    // if checked, then we want to add the label id to the list of selected label ids
    if (event.target.checked) {
      onToggleSelectedChannel(channel.id, true)
    } else {
      // if not checked, then we want to remove the label id from the list of selected label ids
      onToggleSelectedChannel(channel.id, false)
    }
  }
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        padding: '6px 12px',
      }}>
      <Typography
        variant="h6"
        sx={{
          color: COLORS.GRAY_2,
        }}>
        # {channel.name}
      </Typography>
      <StyledCheckboxWithDarkMode
        theme={theme}
        checked={isSelected}
        icon={<CheckBoxOutlineBlankIcon />}
        checkedIcon={
          <CheckBoxIcon
            sx={{
              fill: COLORS.JELLED_PRIMARY_BLUE,
            }}
          />
        }
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onCheckChannel(e)}
      />
    </Stack>
  )
}

const ChannelsList = ({
  enabledChannelIds,
  onToggleSelectedChannel,
  channels,
}: {
  enabledChannelIds: string[]
  onToggleSelectedChannel: (channelId: string, isChecked: boolean) => void
  channels: SourceSlackChannelConfig[]
}) => {
  if (channels.length === 0) {
    return null
  }

  return (
    <Stack
      p="4px 4px 4px 0px"
      sx={{
        background: COLORS.PURE_WHITE,
        borderRadius: '8px',
        maxHeight: '367px',
        overflowY: 'scroll',
      }}>
      {channels.map((channel) => {
        return (
          <ChannelRow
            key={channel.id}
            channel={channel}
            isSelected={enabledChannelIds.includes(channel.id)}
            onToggleSelectedChannel={onToggleSelectedChannel}
          />
        )
      })}
    </Stack>
  )
}

const SlackSettingsModalBase = ({
  onClose,
  updateOnboardingStep,
}: {
  onClose: () => void
  updateOnboardingStep: (steps: UpdateOnboardingStepOption) => void
}) => {
  const [activeOrganization] = useActiveOrganization()
  const setToast = useSetRecoilState(toastAtom)
  const isMobile = useCheckMobileScreen()
  const [user, setUser] = useRecoilState(authenticatedUserAtom)
  const myTwin = useRecoilValue(myTwinAtom)

  // local state for checking if there are changes
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { channels, isFutureChannelsEnabled, isLoading, setIsFutureChannelsEnabled } = useFetchChannelsConfig()
  const [enabledChannelIds, setEnabledChannelIds] = useState<string[]>([])

  // when channels is loaded, we want to set the enabled
  useEffect(() => {
    if (channels.length > 0) {
      setEnabledChannelIds(channels.filter((c) => c.is_enabled).map((c) => c.id))
    }
  }, [channels])

  const onCheckAddFutureChannels = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setIsFutureChannelsEnabled(true)
    } else {
      setIsFutureChannelsEnabled(false)
    }
  }

  const onToggleSelectedChannel = (channelId: string, isChecked: boolean) => {
    if (isChecked) {
      setEnabledChannelIds([...enabledChannelIds, channelId])
    } else {
      setEnabledChannelIds(enabledChannelIds.filter((id) => id !== channelId))
    }
  }

  const isAllSelected = enabledChannelIds.length === channels.length

  const onUpdateSettings = async () => {
    if (!activeOrganization) {
      return
    }

    setIsSubmitting(true)
    try {
      await sourcesUpdateChannelsConfig(activeOrganization, {
        channels: channels.map((c) => {
          return {
            ...c,
            is_enabled: enabledChannelIds.includes(c.id),
          }
        }),
        is_future_channels_enabled: isFutureChannelsEnabled,
      })
      setToast({ title: 'Successfully updated slack channels', severity: 'success' })

      if (!user?.onboarding_steps_completed.includes(OnboardingStep.slack_settings_modal_shown)) {
        if (!user?.onboarding_steps_completed.includes(OnboardingStep.slack_insights_generation_triggered)) {
          if (!myTwin) {
            return
          }

          try {
            await slackBuildInsights(activeOrganization, myTwin.id)
            updateOnboardingStep({
              onboardingSteps: [
                OnboardingStep.slack_insights_generation_triggered,
                OnboardingStep.slack_settings_modal_shown,
              ],
            })
            setToast({ title: 'Successfully started generating slack insights', severity: 'success' })

            // need to update user atom with the new onboarding step
            setUser((prevUser) => {
              if (!prevUser) {
                return prevUser
              }

              return {
                ...prevUser,
                onboarding_steps_completed: [
                  ...prevUser.onboarding_steps_completed,
                  OnboardingStep.slack_settings_modal_shown,
                  OnboardingStep.slack_insights_generation_triggered,
                ],
              }
            })
          } catch (e) {
            setToast({ title: 'Failed to generate slack insights', severity: 'error' })
          }
        } else {
          // if the insights have already been triggered, then we call update onboarding step with just the slack settings modal shown step
          updateOnboardingStep({ onboardingStep: OnboardingStep.slack_settings_modal_shown })
          setUser((prevUser) => {
            if (!prevUser) {
              return prevUser
            }

            return {
              ...prevUser,
              onboarding_steps_completed: [
                ...prevUser.onboarding_steps_completed,
                OnboardingStep.slack_settings_modal_shown,
              ],
            }
          })
        }
      }

      onClose()
    } catch (e) {
      setToast({ title: 'Failed to update slack channels', severity: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderContent = () => {
    if (isLoading) {
      return (
        <Stack height="50vh" width="100%" justifyContent="center" alignItems="center">
          <CircularProgress size="24px" thickness={4} style={{ color: COLORS.JELLED_PRIMARY_BLUE }} />
        </Stack>
      )
    }
    return (
      <SlackSettings
        setHasChanges={() => {}}
        isAllSelected={isAllSelected}
        setEnabledChannelIds={setEnabledChannelIds}
        channels={channels}
        isFutureChannelsEnabled={isFutureChannelsEnabled}
        enabledChannelIds={enabledChannelIds}
        onCheckAddFutureChannels={onCheckAddFutureChannels}
        onToggleSelectedChannel={onToggleSelectedChannel}
        subtitleText="Select the channels in Slack for which you want insights to be generated from. You can modify selections later in Settings."
      />
    )
  }

  return (
    <JelledModal
      onClose={onClose}
      isOpen={true}
      modalStyle={{
        backgroundColor: COLORS.PURE_WHITE,
        marginLeft: isMobile ? '16px' : 0,
        marginRight: isMobile ? '16px' : 0,
        maxWidth: '675px',
      }}>
      <Typography mb={1} variant="h4" color="secondary">
        Slack insights
      </Typography>
      {renderContent()}
      <Stack
        direction={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
        alignItems="center"
        mt={4}
        spacing={isMobile ? 2 : 0}>
        <Stack width="100%" direction="row" alignItems="center" spacing={2} justifyContent={'flex-end'}>
          <Button disabled={isSubmitting} variant="contained" size="medium" color="primary" onClick={onUpdateSettings}>
            Save
          </Button>
        </Stack>
      </Stack>
    </JelledModal>
  )
}

export const SlackSettingsModal = withOnboardingStep(SlackSettingsModalBase, OnboardingStep.slack_settings_modal_shown)

const SlackSettingsPage = ({ updateOnboardingStep }: { updateOnboardingStep: () => void }) => {
  const [activeOrganization] = useActiveOrganization()
  const setToast = useSetRecoilState(toastAtom)
  const isMobile = useCheckMobileScreen()
  const user = useRecoilValue(authenticatedUserAtom)
  const myTwin = useRecoilValue(myTwinAtom)

  const hasGeneratedSlackInsights = user?.onboarding_steps_completed.includes(
    OnboardingStep.slack_insights_generation_triggered
  )

  // local state for checking if there are changes
  const [hasChanges, setHasChanges] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { channels, isFutureChannelsEnabled, isLoading, setIsFutureChannelsEnabled } = useFetchChannelsConfig()
  const [enabledChannelIds, setEnabledChannelIds] = useState<string[]>([])

  // when channels is loaded, we want to set the enabled
  useEffect(() => {
    if (channels.length > 0) {
      setEnabledChannelIds(channels.filter((c) => c.is_enabled).map((c) => c.id))
    }
  }, [channels])

  const onCheckAddFutureChannels = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setIsFutureChannelsEnabled(true)
    } else {
      setIsFutureChannelsEnabled(false)
    }

    setHasChanges(true)
  }

  const onToggleSelectedChannel = (channelId: string, isChecked: boolean) => {
    if (isChecked) {
      setEnabledChannelIds([...enabledChannelIds, channelId])
    } else {
      setEnabledChannelIds(enabledChannelIds.filter((id) => id !== channelId))
    }
    setHasChanges(true)
  }

  const isAllSelected = enabledChannelIds.length === channels.length

  const onUpdateSettings = async () => {
    if (!activeOrganization) {
      return
    }

    setIsSubmitting(true)
    try {
      await sourcesUpdateChannelsConfig(activeOrganization, {
        channels: channels.map((c) => {
          return {
            ...c,
            is_enabled: enabledChannelIds.includes(c.id),
          }
        }),
        is_future_channels_enabled: isFutureChannelsEnabled,
      })
      setToast({ title: 'Successfully updated slack channels', severity: 'success' })

      // if the user has not yet generated slack insights, we want to trigger the slack insights generation
      if (!hasGeneratedSlackInsights) {
        if (!myTwin) {
          return
        }

        try {
          await slackBuildInsights(activeOrganization, myTwin.id)
          updateOnboardingStep()
          setToast({ title: 'Successfully started generating slack insights', severity: 'success' })
        } catch (e) {
          setToast({ title: 'Failed to generate slack insights', severity: 'error' })
        }
      }
    } catch (e) {
      setToast({ title: 'Failed to update slack channels', severity: 'error' })
    } finally {
      setIsSubmitting(false)
      setHasChanges(false)
    }
  }

  if (isLoading) {
    return (
      <PageContainerStack>
        <CircularProgress size="24px" thickness={4} style={{ color: COLORS.JELLED_PRIMARY_BLUE }} />
      </PageContainerStack>
    )
  }

  return (
    <PageContainerStack>
      {!isMobile && (
        <Typography variant="h4" mb={3}>
          Slack
        </Typography>
      )}
      <Typography
        mb={5}
        variant="pS"
        sx={{
          color: COLORS.GRAY_2,
        }}>
        Get summarized insights and chatbot assistance within Slack.
      </Typography>
      <SlackSettings
        setHasChanges={setHasChanges}
        isAllSelected={isAllSelected}
        setEnabledChannelIds={setEnabledChannelIds}
        channels={channels}
        isFutureChannelsEnabled={isFutureChannelsEnabled}
        enabledChannelIds={enabledChannelIds}
        onCheckAddFutureChannels={onCheckAddFutureChannels}
        onToggleSelectedChannel={onToggleSelectedChannel}
        subtitleText="Select the channels in Slack for which you want insights to be generated from."
      />
      {hasChanges && (
        <FloatingButtonBar updateLabel="Save" onUpdateClicked={onUpdateSettings} isUpdating={isSubmitting} />
      )}
    </PageContainerStack>
  )
}

const SlackSettings = ({
  setHasChanges,
  isAllSelected,
  setEnabledChannelIds,
  channels,
  isFutureChannelsEnabled,
  enabledChannelIds,
  onCheckAddFutureChannels,
  onToggleSelectedChannel,
  subtitleText,
}: {
  setHasChanges: (hasChanges: boolean) => void
  isAllSelected?: boolean
  setEnabledChannelIds: (channelIds: string[]) => void
  channels: SourceSlackChannelConfig[]
  isFutureChannelsEnabled: boolean
  enabledChannelIds: string[]
  onCheckAddFutureChannels: (event: React.ChangeEvent<HTMLInputElement>) => void
  onToggleSelectedChannel: (channelId: string, isChecked: boolean) => void
  subtitleText: string
}) => {
  const theme = useTheme()

  return (
    <Stack>
      <Typography variant="h5" mb={1}>
        Channels
      </Typography>
      <Typography
        variant="pXS"
        mb={3}
        sx={{
          color: COLORS.GRAY_3,
        }}>
        {subtitleText}
      </Typography>
      <Stack
        maxWidth="675px"
        sx={{
          background: COLORS.GRAY_8,
        }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1} pr="20px">
          <Typography
            variant="pXS"
            sx={{
              color: COLORS.GRAY_2,
            }}>
            CHANNELS
          </Typography>

          <Button
            variant="text"
            size="small"
            onClick={() => {
              if (isAllSelected) {
                setEnabledChannelIds([])
              } else {
                setEnabledChannelIds(channels.map((c) => c.id))
              }
              setHasChanges(true)
            }}>
            {isAllSelected ? 'Unselect all' : 'Select all'}
          </Button>
        </Stack>

        <ChannelsList
          channels={channels}
          enabledChannelIds={enabledChannelIds}
          onToggleSelectedChannel={onToggleSelectedChannel}
        />
      </Stack>
      <Stack direction="row" alignItems="center" mt={3}>
        <StyledCheckboxWithDarkMode
          theme={theme}
          checked={isFutureChannelsEnabled}
          icon={<CheckBoxOutlineBlankIcon />}
          checkedIcon={
            <CheckBoxIcon
              sx={{
                fill: COLORS.JELLED_PRIMARY_BLUE,
              }}
            />
          }
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onCheckAddFutureChannels(e)}
        />

        <Typography
          variant="pXS"
          sx={{
            color: COLORS.GRAY_1,
          }}>
          Add all future channels you join in Slack to JelledAI for insights streaming
        </Typography>

        <Tooltip title="Not recommended if channels contain sensitive information.">
          <InfoOutlined
            sx={{
              ml: '4px',
              color: COLORS.GRAY_3,
              height: '16px',
              width: '16px',
            }}
          />
        </Tooltip>
      </Stack>
    </Stack>
  )
}

export default withOnboardingStep(SlackSettingsPage, OnboardingStep.slack_insights_generation_triggered)
